import React from 'react';
import { GatsbyImageType } from 'types/common';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { Link } from 'gatsby';
import { createArticleUrl, createReviewUrl } from 'helpers/utils';
import { formatDateToString } from 'utils/formatDateToString';
import { ContentType } from 'utils/enums';
import { Pagination } from 'components/pagination/pagination';
import * as styles from './article-list.module.scss';

type ContentListNode = {
  title: string
  slug: string
  description: string
  primaryImage?: GatsbyImageType
  category: {
    slug: string
  }
  subcategory: {
    slug: string
  }
  createdAt?: string
};

export type ContentListClassNames = {
  wrapperClassName?: string
  containerClassName?: string
  cardContainerClassName?: string
  cardContentClassName?: string
  cardImageClassName?: string
  cardTitleClassName?: string
  cardSubtitleClassName?: string
  cardDescriptionClassName?: string
};

export type ContentListProp = {
  items: ContentListNode[]
  listType: ContentType
  numPages: number
  currentPage: number
  classNames?: ContentListClassNames
};

const ContentList = ({
  items, numPages, currentPage, classNames, listType,
}: ContentListProp) => (
  <div className={classNames?.wrapperClassName || styles.wrapper}>
    <div className={classNames?.containerClassName || styles.container}>
      {items.map((item) => {
        let formattedDate = '';
        if (item.createdAt) {
          formattedDate = formatDateToString(item.createdAt);
        }
        const img = item.primaryImage ? getImage(item.primaryImage.localFile) : undefined;
        return (
          <Link
            className={classNames?.cardContainerClassName || styles.cardContainer}
            key={item.slug}
            to={listType === ContentType.article
              ? createArticleUrl(item.category.slug, item.subcategory.slug, item.slug)
              : createReviewUrl(item.slug)}
          >
            {img && (
              <GatsbyImage
                className={classNames?.cardImageClassName || styles.image}
                alt={item.title}
                image={img}
              />
            )}
            <div className={classNames?.cardContentClassName || ''}>
              <p className={classNames?.cardTitleClassName || styles.title}>
                {item.title}
              </p>
              {item.createdAt && <span className={classNames?.cardSubtitleClassName || ''}>{formattedDate}</span>}
              <span className={classNames?.cardDescriptionClassName || styles.description}>
                {item.description}
              </span>
            </div>
          </Link>
        );
      })}
    </div>
    <Pagination currentPage={currentPage} numPages={numPages} />
  </div>
);

type MixedContentListNode = {
  title: string
  slug: string
  type: 'article' | 'review'
  category: {
    slug: string
  }
  subcategory: {
    slug: string
  }
  description: string
  primaryImage?: GatsbyImageType
  createdAt?: string
};

export type MixedContentListProp = {
  // note: used on templates/topics.tsx
  blogs: MixedContentListNode[]
  numPages: number
  currentPage: number
  classNames?: ContentListClassNames
};

const MixedContentList = ({
  blogs, numPages, currentPage, classNames,
}: MixedContentListProp) => (
  <div className={classNames?.wrapperClassName || styles.wrapper}>
    <div className={classNames?.containerClassName || styles.container}>
      {blogs.map((item) => {
        let formattedDate = '';
        if (item.createdAt) {
          formattedDate = formatDateToString(item.createdAt);
        }
        const img = item.primaryImage ? getImage(item.primaryImage.localFile) : undefined;

        return (
          <Link
            className={classNames?.cardContainerClassName || styles.cardContainer}
            key={item.slug}
            to={item.type === ContentType.article
              ? createArticleUrl(item.category.slug, item.subcategory.slug, item.slug)
              : createReviewUrl(item.slug)}
          >
            {img && (
              <GatsbyImage
                className={classNames?.cardImageClassName || styles.image}
                alt={item.title}
                image={img}
              />
            )}
            <div className={classNames?.cardContentClassName || ''}>
              <p className={classNames?.cardTitleClassName || styles.title}>{item.title}</p>
              {item.createdAt && <span className={classNames?.cardSubtitleClassName || ''}>{formattedDate}</span>}
              <span className={classNames?.cardDescriptionClassName || styles.description}>
                {item.description}
              </span>
            </div>
          </Link>
        );
      })}
    </div>
    <Pagination currentPage={currentPage} numPages={numPages} />
  </div>
);

export { ContentList, MixedContentList };
export type { ContentListNode, MixedContentListNode };
